import { useState } from 'react';
import { PASSWORD_RULES, passwordIssues } from '@mana/shared';
import { useAuth } from '../lib/auth-context.js';
import { Button } from '../ui/Button.js';
import { Field, Input } from '../ui/Input.js';
import { Logo } from '../ui/Logo.js';

export function SignInPage() {
  const { signIn, signUp } = useAuth();
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  // Locked to the company for now — prefilled and not editable in the form.
  const organizationName = 'MANA HOME LOANS LLC';
  const [nmls, setNmls] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<{
    email: string;
    text: string;
    delivered: boolean;
    nmlsReview?: boolean;
    devUrl?: string;
  } | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setPendingMessage(null);
    setSubmitting(true);
    try {
      if (mode === 'signin') {
        await signIn(email, password);
      } else {
        if (passwordIssues(password).length > 0) {
          setError('Your password does not meet the requirements listed below.');
          return;
        }
        const result = await signUp({
          organizationName,
          name,
          email,
          password,
          nmls: nmls.trim() || undefined,
        });
        setPendingMessage({
          email: result.email,
          text: result.message,
          delivered: result.delivered ?? false,
          nmlsReview: result.nmlsReview,
          devUrl: result.devVerificationUrl,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid h-full grid-cols-1 lg:grid-cols-[1fr_minmax(420px,520px)]">
      {/* Hero panel */}
      <div className="relative hidden overflow-hidden bg-brand-navy-800 lg:block">
        {/* Decorative gold sweep */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-30"
          style={{
            background:
              'radial-gradient(circle at 20% 20%, rgba(184,150,90,0.4), transparent 50%), radial-gradient(circle at 80% 80%, rgba(184,150,90,0.25), transparent 50%)',
          }}
        />
        <div
          aria-hidden
          className="absolute inset-y-0 right-0 w-1/2 origin-bottom-right -skew-x-12 bg-gradient-to-br from-brand-navy-700 to-brand-navy-900"
        />
        <div className="relative flex h-full flex-col justify-between px-12 py-12 text-white">
          <Logo variant="full" tone="navy" size={48} />

          <div className="max-w-md space-y-6">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-gold-300">
              MLO One-Stop Platform
            </p>
            <h1 className="font-display text-4xl font-bold leading-tight text-white">
              Built for the way Mana MLOs actually work.
            </h1>
            <p className="text-base leading-relaxed text-brand-navy-100">
              Live UWM pipeline. Assignment-aware workflow automation. Audited,
              encrypted, and ready for the next ten years of growth.
            </p>
            <ul className="space-y-2 text-sm text-brand-navy-100">
              <li className="flex items-start gap-3">
                <CheckIcon /> UWM Insights API integration with delta sync every hour
              </li>
              <li className="flex items-start gap-3">
                <CheckIcon /> MyLoanTracker workflow with email + WhatsApp notifications
              </li>
              <li className="flex items-start gap-3">
                <CheckIcon /> Role and module-level access control, audit logged
              </li>
            </ul>
          </div>

          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-gold-300/80">
            NMLS#2701164 · Mana Home Loans LLC
          </div>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center bg-brand-cream px-6 py-10 lg:px-12">
        <form
          onSubmit={submit}
          className="w-full max-w-sm space-y-5 rounded-2xl border border-slate-200/70 bg-white p-8 shadow-soft"
        >
          <div className="flex flex-col items-start gap-3 lg:hidden">
            <Logo variant="full" tone="light" size={40} />
          </div>

          <div>
            <h2 className="font-display text-2xl font-bold text-brand-navy-800">
              {mode === 'signin' ? 'Welcome back' : 'Create your workspace'}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {mode === 'signin'
                ? 'Sign in to access your pipeline and workflows.'
                : 'Create a new organization for your team.'}
            </p>
          </div>

          {mode === 'signup' && (
            <>
              <Field label="Organization name" hint="Set by your company — not editable.">
                <Input value={organizationName} readOnly disabled className="bg-slate-100 text-slate-500" />
              </Field>
              <Field
                label="Full name"
                hint="Enter it exactly as it appears in your NMLS record — we verify it."
              >
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane MLO"
                  required
                />
              </Field>
              <Field
                label="NMLS number"
                hint="Verified against official NMLS records — your name, NMLS ID, and Mana Home Loans affiliation must match."
              >
                <Input
                  value={nmls}
                  onChange={(e) => setNmls(e.target.value.replace(/\D/g, ''))}
                  inputMode="numeric"
                  placeholder="e.g. 1234567"
                />
              </Field>
            </>
          )}

          <Field
            label="Email"
            hint={
              mode === 'signup'
                ? 'New accounts must use a @manahomeloans.com email address.'
                : undefined
            }
          >
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </Field>

          <Field label="Password">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
              required
            />
          </Field>

          {mode === 'signup' && password.length > 0 && (
            <ul className="-mt-2 space-y-0.5 text-[11px]">
              {PASSWORD_RULES.map((rule) => {
                const met = rule.test(password);
                return (
                  <li key={rule.label} className={met ? 'text-emerald-600' : 'text-slate-400'}>
                    <span className="mr-1">{met ? '✓' : '•'}</span>
                    {rule.label}
                  </li>
                );
              })}
            </ul>
          )}

          {error && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              {error}
            </div>
          )}

          {pendingMessage && (
            <div
              className={
                'rounded-lg border px-3 py-3 text-xs text-slate-700 ' +
                (pendingMessage.nmlsReview
                  ? 'border-sky-300 bg-sky-50'
                  : pendingMessage.delivered
                    ? 'border-brand-gold-200 bg-brand-gold-50/40'
                    : 'border-amber-300 bg-amber-50')
              }
            >
              <div className="font-semibold text-brand-navy-800">
                {pendingMessage.nmlsReview
                  ? 'Account created — awaiting NMLS verification'
                  : pendingMessage.delivered
                    ? 'Check your inbox — verification required'
                    : 'Account created — verification email not sent'}
              </div>
              <p className="mt-1">{pendingMessage.text}</p>
              <p className="mt-1 text-slate-500">
                {pendingMessage.delivered ? 'Sent to' : 'For'}{' '}
                <code className="font-mono">{pendingMessage.email}</code>.
              </p>
              {pendingMessage.devUrl && (
                <p className="mt-2 break-all">
                  <strong>Dev shortcut</strong> (no SMTP configured):{' '}
                  <a
                    href={pendingMessage.devUrl}
                    className="text-brand-navy-700 underline"
                  >
                    open verification link
                  </a>
                </p>
              )}
            </div>
          )}

          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? 'Working…' : mode === 'signin' ? 'Sign in' : 'Create account'}
          </Button>
          {submitting && mode === 'signup' && (
            <p className="text-center text-[11px] text-slate-500">
              Verifying your details against official NMLS records — this can take up to a minute.
            </p>
          )}

          <div className="flex items-center justify-between text-xs">
            <button
              type="button"
              onClick={() => setMode(mode === 'signin' ? 'signup' : 'signin')}
              className="font-medium text-brand-navy-700 hover:text-brand-navy-900"
            >
              {mode === 'signin' ? 'Create an account →' : '← Back to sign in'}
            </button>
            <span className="text-slate-400">v0.1.0</span>
          </div>
        </form>
      </div>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      className="mt-0.5 shrink-0 text-brand-gold-400"
      aria-hidden
    >
      <path
        d="M3.5 8.5l3 3 6-7"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
